import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Title } from '@angular/platform-browser';
import { ExercicesExerciceViewDialogComponent } from '../../components/exercice-view-dialog/exercice-view-dialog';
import { CurrentUserRolesProvider } from '../../../organizations/providers/current-user-roles-provider';
import { ExercicesFormComponent } from '../../components/form/form';
import { ExerciceRow, exerciceOrderByOptions, ExercicesProvider } from '../../providers/exercices-provider';
import { ExerciceRights } from '../../providers/exercice-rights';
import { LibraryService } from '../../../libraries/providers/library.services';
import { BreadcrumbProvider, Crumb } from '../../../breadcrumb/providers/breadcrumb-provider';
import { forkJoin, fromEvent, Observable, Subject, Subscription, takeUntil } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map } from 'rxjs/operators';
import { MatSort } from '@angular/material/sort';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ModuleMenu } from "../../../common/models/menu";
import { Organization } from "../../../common/models/organization";
import { Library } from "../../../common/models/library";
import { User } from "../../../common/models/user";
import { SimpleMatDataSource } from "../../../common/providers/simple-mat-data-source";
import { ExerciseDatasource } from "../../../common/datasource/exercise.datasource";
import { ConfigProvider } from "../../../config-provider.service";
import { CurrentUser } from "../../../common/providers/current-user";
import { UtilsProvider } from "../../../common/providers/utils-provider.service";
import { ConfirmDialogComponent } from "../../../common/components/confirm-dialog/confirm-dialog";
import { ModuleContentComponent } from "../../../common/components/module-content/module-content";
import { Error401Component } from "../../../common/components/error-401/error-401";
import { SideMenuComponent } from "../../../common/components/side-menu/side-menu";
import { PageContentComponent } from "../../../common/components/page-content/page-content";
import { HeaderComponent } from "../../../common/components/header-elitment/header-elitment";
import { Error404Component } from "../../../common/components/error-404/error-404";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatMenuModule } from "@angular/material/menu";
import { MatTableModule } from "@angular/material/table";
import { NgForOf, NgIf } from "@angular/common";
import { MatInputModule } from "@angular/material/input";
import { LibrariesSideMenuComponent } from "../../../libraries/components/side-menu/side-menu";
import {
  ExerciceCategoryThumbnailComponent
} from "../../components/exercice-category-thumbnail/exercice-category-thumbnail";
import { ApiPaginator } from "../../../common/components/api-paginator/api-paginator";
import { CenterLoaderComponent } from "../../../common/components/center-loader/center-loader";
import { ExerciceCategoryFiltersComponent } from "../../components/exercice-category-filters/exercice-category-filters";
import { MatIconButton } from "@angular/material/button";
import { ExerciceCategorySortingComponent } from "../../components/exercice-category-sorting/exercice-category-sorting";
import { ExerciceListCardComponent } from "../../components/exercice-list-card/exercice-list-card";
import { ExerciceListShowDialogComponent } from "../../components/exercice-list-show-dialog/exercice-list-show-dialog";

@Component({
  selector: 'exercices-list-page',
  providers: [],
  styleUrls: ['./list.scss'],
  standalone: true,
  imports: [
    MatPaginator,
    ModuleContentComponent,
    Error401Component,
    SideMenuComponent,
    PageContentComponent,
    HeaderComponent,
    Error404Component,
    MatFormFieldModule,
    MatIconModule,
    MatMenuModule,
    MatTableModule,
    ReactiveFormsModule,
    NgIf,
    MatIconButton,
    MatInputModule,
    TranslateModule,
    LibrariesSideMenuComponent,
    ExerciceCategoryThumbnailComponent,
    NgForOf,
    ApiPaginator,
    CenterLoaderComponent,
    ExerciceCategoryFiltersComponent,
    ExerciceCategoryFiltersComponent,
    ExerciceCategorySortingComponent,
    ExerciceListCardComponent,
    ExerciceListCardComponent,
  ],
  templateUrl: './list.html'
})
export class ExercicesListPageComponent implements OnInit, AfterViewInit, OnDestroy {

  public DESCRIPTION_MAX_LENGTH = 5000;

  public pageTitle = '';
  public mainActions: ModuleMenu[] = [];
  public isLoading = true;
  public is401 = false;
  public is404 = false;

  public organization: Organization = {} as Organization;
  public library: Library = {} as Library;
  public rights: ExerciceRights = new ExerciceRights(this._utils);

  public dataSource: SimpleMatDataSource = new SimpleMatDataSource([]);
  public columns: string[] = ['image', 'name', 'accents', 'tags', 'creater', 'actions'];

  public exerciseDataSource: ExerciseDatasource = {} as ExerciseDatasource;

  private filterChange$ = new Subject<void>();
  public selectedCategoryIds: number[] = [];

  public orderBy: exerciceOrderByOptions = exerciceOrderByOptions.created_desc;

  @ViewChild(MatPaginator, {static: false}) public paginator: MatPaginator | null = null;
  @ViewChild(MatSort, {static: false}) public sorter: MatSort | null = null;
  @ViewChild('search', {static: false}) public search: ElementRef | undefined;
  @ViewChild('searchIcon') searchIcon: ElementRef | undefined;
  private destroy$ = new Subject<void>();

  private subscriptions: Subscription[] = [];

  constructor(
    public conf: ConfigProvider,
    public breadcrumbProvider: BreadcrumbProvider,
    public currentUser: CurrentUser,
    private _utils: UtilsProvider,
    private _title: Title,
    private _router: Router,
    private _toaster: MatSnackBar,
    private _currentUserRoles: CurrentUserRolesProvider,
    private _route: ActivatedRoute,
    private _translater: TranslateService,
    private _dialog: MatDialog,
    private _exercicesProvider: ExercicesProvider,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
  }

  public ngOnInit(): void {
    this.subscriptions.push(
      this._route.data.subscribe((data: any) => {
        this.organization = data.organization;
        this.library = data.library;
        this.createPageTitle(data);
        this.createBreadcrumb();
        this.rights.evaluateScreenRights({
          rolesIds: this._currentUserRoles.getRolesIds(this.organization?.id!)
        });

        if (this.rights.ADD) {
          this.mainActions = [
            { title: 'Add', icon: 'add', url: null, onClick: () => this.onAddExerciceClicked() }
          ];
        }

        this.exerciseDataSource = new ExerciseDatasource(this._exercicesProvider);
        if (!this.library?.id) {
          console.error('Library id is required');
          return;  // Or handle more gracefully
        }


        const filterKey = this._route.snapshot.routeConfig?.path?.split('/').pop()
        const isUserContext = data.context === LibraryService.CONTEXT_USER;
        const loadObservable$: Observable<void> = isUserContext
          ? this.exerciseDataSource.loadExerciseRowsByUser(this.organization!, this.currentUser.user!, [], this.library, true, false, true, 1, undefined, undefined, undefined, this.orderBy)
          : this.exerciseDataSource.loadExerciseRows(this.organization!, [], this.library, true, false, true, 1, undefined, undefined, undefined, this.orderBy, filterKey);

        loadObservable$.subscribe({
          next: () => {
            this.updateTableData();
            this.isLoading = false;
          },
          error: (error) => {
            this.onError(error);
          }
        });
      })
    );
  }

  public ngAfterViewInit() {
    // trigger aux keys inputs
    if (this.search && this.search.nativeElement) {
      fromEvent<KeyboardEvent>(this.search.nativeElement, 'keyup').pipe(
        debounceTime(150),
        filter((e: KeyboardEvent) => e.key === 'Enter' || e.keyCode === 13),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
      ).subscribe((searchTerm) => {
        this.triggerSearch();
      });
    } else {
      console.error('Search input not found');
    }

    // trigger au click sur les filtres
    this.filterChange$.pipe(
      debounceTime(150),
      takeUntil(this.destroy$),
    ).subscribe(() => {
      this.triggerSearch();
    });

    this.paginator?.page.subscribe(() => this.loadExercisePaginated());
  }

  protected triggerSearch() {
    this.paginator?.firstPage();
    this.loadExercisePaginated();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  public onShowClicked(row: ExerciceRow): void {
    const dialogRef: any = this._dialog.open(ExerciceListShowDialogComponent, {
      data: {
        exerciceRow: row,
        organization: this.organization
      }
    });
  }

  public onAddExerciceClicked(): void {
    const dialogRef: any = this._dialog.open(ExercicesFormComponent, {
      data: {
        organization: this.organization,
        formTitle: 'Exercise_creation',
        confirmButtonTitle: this._translater.instant('Save'),
        libraryId: this.library!.id
      }
    });

    this.subscriptions.push(
      dialogRef.afterClosed().subscribe((row: ExerciceRow) => {
        if (row) {
          this.loadExercisePaginated();

          /* this.dataSource.push(row);
          this._exercicesProvider.addImageToRow(this.organization.id, row);*/
        }
      })
    );
  }

  public onModifyExerciceClicked(toModify: ExerciceRow): void {
    const dialogRef: any = this._dialog.open(ExercicesFormComponent, {
      data: {
        organization: this.organization,
        exerciseRow: toModify,
        formTitle: 'Exercise_edition',
        confirmButtonTitle: this._translater.instant('Save')
      }
    });

    this.subscriptions.push(
      dialogRef.afterClosed().subscribe((row: ExerciceRow) => {
        if (row) {
          this.loadExercisePaginated();
        }

        /* if (row) {
            Promise.all([
                this._exercicesProvider.getImageFile(this.organization.id, row, 'dataUri', true)
            ]).then((media) => {

                row.exerciceImage = media[0];

                for (let i = 0; i < this.dataSource.count; i++) {
                    if (this.dataSource.get(i).exercice.id === row.exercice.id) {

                        this.dataSource.set(i, row);
                        return;

                    }
                }
            });
        }*/
      })
    );
  }

  public onDuplicateExerciceClicked(toDuplicate: ExerciceRow): void {
    if (!this.organization?.id) {
      console.error('Organization id is required');
      return;
    }
    forkJoin([
      this._exercicesProvider.getImageFile(this.organization.id, toDuplicate, 'file'),
      this._exercicesProvider.getVideoFile(this.organization.id, toDuplicate, 'file'),
      this._exercicesProvider.getImageFile(this.organization.id, toDuplicate),
    ]).pipe(
      map(([image, video, imageLink]: any[]) => {
        const exerciseRow: ExerciceRow = {
          exercice: Object.assign({}, toDuplicate.exercice),
          categories: toDuplicate.categories.slice(),
          exerciceImage: imageLink,
          exerciceVideo: video,
          creater: this.currentUser.user,
        };

        exerciseRow.exercice.id = 0;
        exerciseRow.exercice.name = exerciseRow.exercice.name + ' (1)';

        exerciseRow.exercice.image = image;

        const dialogRef: any = this._dialog.open(ExercicesFormComponent, {
          data: {
            organization: this.organization,
            exerciseRow,
            formTitle: 'Exercise_duplication',
            skipLangSelection: true,
            confirmButtonTitle: 'Duplicate',
            exerciceLibraryId: this.library!.id
          }
        });

        this.subscriptions.push(
          dialogRef.afterClosed().subscribe((row: ExerciceRow) => {
            if (row) {
              this.loadExercisePaginated();
            }
            /* if (row) {

                Promise.all([
                    this._exercicesProvider.getImageFile(this.organization.id, row)
                ]).then((media) => {

                    row.exerciceImage = media[0];

                    this.dataSource.push(row);

                });
            }*/
          })
        );
      })
    ).subscribe();
  }

  public onDeleteExerciceClicked(toDelete: ExerciceRow): void {
    const dialog: MatDialogRef<any> = this._dialog.open(ConfirmDialogComponent, {
      data: {
        confirmText: this._translater.instant('Confirm_delete_exercice')
      }
    });
    this.subscriptions.push(
      dialog.afterClosed().subscribe((confirmed: boolean) => {
        if (confirmed) {
          this._exercicesProvider.deleteExercice(this.organization.id!, toDelete.exercice).subscribe({
            next: () => {
              this.loadExercisePaginated();
              this._toaster.open(this._translater.instant('Exercice_deleted'), '', { duration: 5000 });
            },
            error: (error: any) => {
              this._toaster.open(this._translater.instant('Exercice_delete_error'), '', { duration: 5000 });
            }
          });
        }
      })
    );
  }

  private createPageTitle(data:any) {
    if (!data.context) return;

    // Detect the current route context
    const filterKey = this._route.snapshot.routeConfig?.path?.split('/').pop();

    if (filterKey === 'exercises-elitment') {
      this.pageTitle = this._translater.instant('Elitment_exercises_title');
    } else if (filterKey === 'exercises-super-coach') {
      this.pageTitle = this._translater.instant('Super_coaches_exercises_title');
    } else if (filterKey === 'personal' || data.context === LibraryService.CONTEXT_USER) {
      this.pageTitle = this._translater.instant('Personal_exercises_title');
    } else {
      // Fallback: coaches exercises
      this.pageTitle = this._translater.instant('Coaches_exercises_title');
    }

    this._title.setTitle(this.pageTitle + ' | ' + this.conf.platformName);
  }

  private createBreadcrumb() {

    const crumbs: Crumb[] = [
      {
        shortTitle: 'Exercises',
        title: this.pageTitle,
        url: this._router.url
      }
    ];
    this.breadcrumbProvider.setDefaultCrumbs(crumbs);
  }

  private onError(error: any): void {
    this.isLoading = false;

    if (error.status === 404) {
      this.is404 = true;
    } else if (error.status === 401) {
      this.is401 = true;
    }
  }

  private loadExercisePaginated(): void {
    this.isLoading = true;
    const pageIndex = this.paginator!.pageIndex + 1;
    const pageSize = this.paginator!.pageSize;
    const keyword = this.search!.nativeElement.value;

    const filterKey = this._route.snapshot.routeConfig?.path?.split('/').pop()
    const isUserContext = this._route.snapshot.data['context'] === LibraryService.CONTEXT_USER;

    const loadObservable$: Observable<void> = isUserContext
      ? this.exerciseDataSource!.loadExerciseRowsByUser(
        this.organization!,
        this.currentUser.user!,
        [],
        this.library,
        true,
        false,
        true,
        pageIndex,
        pageSize,
        keyword,
        this.selectedCategoryIds,
        this.orderBy,
      ) : this.exerciseDataSource!.loadExerciseRows(
        this.organization!,
        [],
        this.library,
        true,
        false,
        true,
        pageIndex,
        pageSize,
        keyword,
        this.selectedCategoryIds,
        this.orderBy,
        filterKey
      );

    loadObservable$.subscribe({
      next: () => {
        this.updateTableData();
        this.isLoading = false;
      },
      error: (err: any) => {
        this.onError(err);
      }
    });
  }

  private updateTableData(): void {
    this.dataSource.data = this.exerciseDataSource.subject.getValue();
    this.dataSource._updateChangeSubscription(); // Explicitly inform the table that the data has changed
    this.changeDetectorRef.detectChanges();
  }


  protected onOrderBySelection(orderBy: exerciceOrderByOptions): void {
    // Émettre un changement de filtre
    this.orderBy = orderBy;

    this.filterChange$.next();
  }

  protected onCategoryClicked(categoryId: number|undefined): void {
    // si pas de category id => on affiche tout
    if (!categoryId) {
      this.selectedCategoryIds = [];
    } else {
      // sinon on ajoute ou retire la catégorie
      if (this.selectedCategoryIds.includes(categoryId)) {
        this.selectedCategoryIds = this.selectedCategoryIds.filter((id) => id !== categoryId);
      } else {
        this.selectedCategoryIds.push(categoryId);
      }
    }
    // Émettre un changement de filtre
    this.filterChange$.next();
  }
}
